import { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Optional label so the fallback can name which area failed (e.g. "Trading"). */
  area?: string
}

interface State {
  error: Error | null
}

/**
 * Catches render-time errors so a single bad page can't white-screen the whole
 * app — important here because a crash on any tab would also hide the user's
 * live positions and order controls. Shows a themed fallback with a reload.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Surface to the console for debugging; no remote logging wired here.
    console.error('[ErrorBoundary]', this.props.area ?? 'app', error, info.componentStack)
  }

  handleReload = () => {
    this.setState({ error: null })
    window.location.reload()
  }

  render() {
    if (!this.state.error) return this.props.children

    const { area } = this.props
    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          minHeight: '60vh',
          padding: 32,
          textAlign: 'center',
        }}
      >
        <div
          className="eyebrow"
          style={{ color: 'var(--red)', letterSpacing: '0.24em' }}
        >
          Signal lost
        </div>
        <h2 style={{ fontSize: '1.2rem', color: 'var(--text)' }}>
          {area ? `The ${area} view hit a snag` : 'Something glitched'}
        </h2>
        <p
          style={{
            fontSize: '0.8rem',
            color: 'var(--muted)',
            maxWidth: 420,
            lineHeight: 1.5,
          }}
        >
          The rest of the app is unaffected. Reload to get this panel back —
          your positions and orders are safe on the server.
        </p>
        {this.state.error?.message && (
          <code
            className="mono"
            style={{
              fontSize: '0.62rem',
              color: 'var(--muted)',
              background: 'var(--stamp)',
              border: '1px solid var(--edge)',
              borderRadius: 3,
              padding: '6px 10px',
              maxWidth: 480,
              overflowWrap: 'anywhere',
            }}
          >
            {this.state.error.message}
          </code>
        )}
        <button
          onClick={this.handleReload}
          style={{
            marginTop: 6,
            background: 'var(--cash)',
            color: 'var(--ink)',
            border: 'none',
            borderRadius: 3,
            padding: '8px 18px',
            fontFamily: "'Syne Mono', monospace",
            fontSize: '0.7rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    )
  }
}
